/**
 * Zdjęcia po stronie urządzenia.
 *
 * Kompresja jest wymogiem, nie optymalizacją (CLAUDE.md §6): zdjęcie z telefonu ma
 * dziś 4-8 MB, a serwisant w hali bywa bez zasięgu — do kolejki wysyłkowej i do
 * IndexedDB trafia obraz zmniejszony do 1600 px i JPEG q≈0.75, czyli ~150-400 kB.
 * Bez tego pierwsza wizyta bez zasięgu zapycha pamięć przeglądarki.
 *
 * Wszystko dzieje się w przeglądarce, na kanwie. Żadnej biblioteki — `createImageBitmap`
 * i `canvas.toBlob` są w każdej przeglądarce, na której nam zależy.
 */

/** Dłuższy bok po zmniejszeniu. Wartość z CLAUDE.md §6, nie z sufitu. */
const MAX_BOK = 1600;
const JAKOSC = 0.75;

export interface PrzygotowaneZdjecie {
  /** Sam base64, bez przedrostka `data:` — tego oczekuje API. */
  dane: string;
  mime: string;
  bytes: number;
  szerokosc: number;
  wysokosc: number;
  /** Adres do podglądu w `<img>`; zwalniany przez `zwolnijPodglad`. */
  podglad: string;
}

/**
 * Zmniejsza i przekodowuje plik z aparatu albo z dysku.
 *
 * PDF-y i inne dokumenty przechodzą bez zmian — kompresować da się obraz, nie DTR.
 */
export async function przygotujZdjecie(plik: File): Promise<PrzygotowaneZdjecie> {
  if (!plik.type.startsWith('image/')) {
    const bufor = await plik.arrayBuffer();
    return {
      dane: doBase64(bufor),
      mime: plik.type,
      bytes: bufor.byteLength,
      szerokosc: 0,
      wysokosc: 0,
      podglad: URL.createObjectURL(plik),
    };
  }

  const obraz = await createImageBitmap(plik);
  const skala = Math.min(1, MAX_BOK / Math.max(obraz.width, obraz.height));
  const szerokosc = Math.round(obraz.width * skala);
  const wysokosc = Math.round(obraz.height * skala);

  const kanwa = document.createElement('canvas');
  kanwa.width = szerokosc;
  kanwa.height = wysokosc;

  const pedzel = kanwa.getContext('2d');
  if (!pedzel) throw new Error('Przeglądarka nie udostępniła kanwy do przeskalowania zdjęcia.');
  pedzel.drawImage(obraz, 0, 0, szerokosc, wysokosc);
  obraz.close();

  const blob = await new Promise<Blob | null>((gotowe) =>
    kanwa.toBlob(gotowe, 'image/jpeg', JAKOSC),
  );
  if (!blob) throw new Error('Nie udało się przygotować zdjęcia.');

  const bufor = await blob.arrayBuffer();
  return {
    dane: doBase64(bufor),
    mime: 'image/jpeg',
    bytes: bufor.byteLength,
    szerokosc,
    wysokosc,
    podglad: URL.createObjectURL(blob),
  };
}

export function zwolnijPodglad(podglad: string): void {
  if (podglad.startsWith('blob:')) URL.revokeObjectURL(podglad);
}

/**
 * Base64 bez `FileReader`, żeby nie żonglować zdarzeniami. Porcjami po 32 kB,
 * bo `String.fromCharCode` z całą tablicą naraz przewraca się na dużych plikach.
 */
function doBase64(bufor: ArrayBuffer): string {
  const bajty = new Uint8Array(bufor);
  const porcja = 0x8000;
  let tekst = '';

  for (let i = 0; i < bajty.length; i += porcja) {
    tekst += String.fromCharCode(...bajty.subarray(i, i + porcja));
  }

  return btoa(tekst);
}

/** Rozmiar po ludzku — „312 kB" zamiast „319488". */
export function rozmiar(bajty: number): string {
  if (bajty < 1024) return `${bajty} B`;
  if (bajty < 1024 * 1024) return `${Math.round(bajty / 1024)} kB`;
  return `${(bajty / 1024 / 1024).toFixed(1)} MB`;
}
